import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { insertTimestampIntoText } from "../../helpers/getTimestamp";
import {
    getClientServiceIntervalConfig,
    normalizeClientServiceInterval,
} from "../../helpers/intervalTypesMap";
import { getClientManagers } from "../../services/client/getClientManagers";
import { cloneClientServiceSubscription } from "../../services/clientService/cloneClientServiceSubscription";
import { getLoggedInEmployee } from "../../services/getLoggedInEmployee";
import ButtonAddTimestamp from "../../utils/ButtonAddTimestamp";
import AssignedByListBox from "../clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/AssignedByListBox";
import InputTextAreaModal from "../clientServiceSubscriptionTask/updateClientServiceSubscriptionTaskModal/InputTextAreaModal";
import { getClientServiceMessage } from "../../helpers/clientServiceMessage";

export default function CloneSubscriptionModal({
    open,
    setOpen,
    clientServiceId,
    refreshSubs,
    clientService,
    clientId,
}) {
    const textAreaRef = useRef(null);
    const [displayNotes, setDisplayNotes] = useState(false);
    const [loggedInEmployee, setLoggedInEmployee] = useState({});
    const [form, setForm] = useState({
        clientServiceId: clientServiceId,
        serviceYear: new Date().getFullYear(),
        start: new Date().toISOString().split("T")[0],
        end: new Date().toISOString().split("T")[0],
        //interval: "Weekly",
        search: "d",
        /* assignedEmployees: [], */
        notes: "",
        isEveryItemRenewable: false,
        cloningMethod: "SERVICE",
        assignedEmployee: null,
    });
    const [managers, setManagers] = useState(null);

    useEffect(() => {
        if (!clientService) return;

        const calculateNextDate = (intervalType) => {
            const intervalConfig = getClientServiceIntervalConfig(intervalType);

            if (
                !intervalConfig ||
                typeof intervalConfig.days !== "number" ||
                Number.isNaN(intervalConfig.days)
            ) {
                console.error(
                    "Invalid subscription interval configuration:",
                    intervalType,
                );
                return;
            }

            const nextDate = new Date();
            nextDate.setDate(nextDate.getDate() + intervalConfig.days);

            if (Number.isNaN(nextDate.getTime())) {
                console.error(
                    "Invalid next subscription date calculated for interval:",
                    intervalType,
                );
                return;
            }

            setForm((prev) => ({
                ...prev,
                interval: normalizeClientServiceInterval(intervalType),
                end: nextDate.toISOString().split("T")[0],
                serviceYear: nextDate.getFullYear(),
            }));
            return;
        };

        calculateNextDate(clientService.interval);
    }, [clientService]);

    const toggleNotes = () => {
        setDisplayNotes(!displayNotes);
    };

    const getNewLoggedInEmployee = useCallback(async () => {
        const currentEmployee = await getLoggedInEmployee();
        if (currentEmployee.success) {
            setLoggedInEmployee(currentEmployee.data.employeeResponse);
            console.log(currentEmployee);
        }
    }, []);

    const onHandleClickAddTimestamp = (fieldName) => {
        const textarea = textAreaRef.current;
        if (!textarea) return;

        const { selectionStart, selectionEnd } = textarea;
        const value = form[fieldName] || "";

        const { caretPosition, value: newValue } = insertTimestampIntoText(
            value,
            selectionStart,
            selectionEnd,
            loggedInEmployee,
        );

        setForm({
            ...form,
            [fieldName]: newValue,
        });

        setTimeout(() => {
            textarea.focus();
            textarea.setSelectionRange(caretPosition, caretPosition);
        }, 0);
    };

    async function handleSubmit() {
        if (form.cloningMethod == null) {
            toast.error("Choose a cloning method please");
            return;
        }
        if (form.start == null) {
            toast.error("Choose the assigned date");
            return;
        }
        if (form.assignedEmployee == null) {
            toast.error("Assign an employee to the service");
            return;
        }
        const data = {
            clientServiceId: clientServiceId,
            interval: form.interval,
            serviceYear: form.serviceYear,
            start: new Date().toISOString().split("T")[0],
            end: form.end,
            /* assignedEmployees: form.assignedEmployees, */
            notes: form.notes,
            assignedByEmployee: loggedInEmployee,
            cloningMethod: form.cloningMethod,
            isEveryItemRenewable: form.isEveryItemRenewable,
            assignedEmployee: form.assignedEmployee,
        };

        const response = await cloneClientServiceSubscription(data);
        if (response.data.success) {
            toast.success(
                getClientServiceMessage(
                    response.data.message,
                    "Service cloned successfully.",
                ),
            );
            await refreshSubs();
            setOpen(false);
        } else {
            toast.error(
                getClientServiceMessage(
                    response.data.message,
                    "The service could not be cloned.",
                ),
            );
        }
    }

    useEffect(() => {
        if (!open) return;
        getNewLoggedInEmployee();
    }, [open, getNewLoggedInEmployee]);

    const loadManagers = useCallback(async () => {
        if (!clientId) return;
        const response = await getClientManagers(clientId);
        const list = response?.data?.managers ?? [];
        const originalAssignedEmployeeId =
            clientService?.assignedEmployee?.employeeId ??
            clientService?.assignedEmployeeId;
        const matchedManager = list.find(
            (manager) => manager.employeeId === originalAssignedEmployeeId
        );
        setManagers(list);
        setForm((prev) => {
            if (matchedManager) {
                return { ...prev, assignedEmployee: matchedManager };
            }

            if (
                prev.assignedEmployee &&
                !list.some(
                    (manager) =>
                        manager.employeeId === prev.assignedEmployee.employeeId
                )
            ) {
                return { ...prev, assignedEmployee: null };
            }

            return prev;
        });
    }, [clientId, clientService]);

    useEffect(() => {
        if (open) loadManagers();
    }, [open, loadManagers]);

    const onHandleChangeAssignEmployee = (newAssignedEmployee) => {
        setForm({
            ...form,
            cloningMethod: "SERVICE",
            assignedEmployee: newAssignedEmployee,
        });
    };

    const onDueDateChange = (e) => {
        if (form.start > e.target.value) {
            toast.error("The due date cannot be before the assigned date.");
            return;
        }
        setForm({ ...form, end: e.target.value });
    };

    return (
        <section>
            <Toaster />
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={setOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex items-end justify-center min-h-full p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative w-full px-4 pt-5 pb-4 overflow-hidden text-left transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:max-w-sm sm:p-6">
                                    <div className="text-left">
                                        <div className="flex justify-between">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-lg font-medium leading-6 text-gray-900"
                                            >
                                                Service Cloning
                                            </Dialog.Title>
                                            <button onClick={() => setOpen(false)}>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-5 h-5"
                                                    viewBox="0 0 20 20"
                                                    fill="currentColor"
                                                >
                                                    <path
                                                        fillRule="evenodd"
                                                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                        clipRule="evenodd"
                                                    />
                                                </svg>
                                            </button>
                                        </div>

                                        <div className="mt-5 sm:mt-6">
                                            <h1 className="font-normal text-md">
                                                Year
                                            </h1>
                                            <input
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        serviceYear: e.target.value,
                                                    })
                                                }
                                                className="relative inline-block w-full text-left"
                                                type="number"
                                                min="2000"
                                                max="2100"
                                                step="1"
                                                value={form.serviceYear}
                                            />
                                        </div>

                                        {/* <div className="flex flex-col mt-5 sm:mt-6">
                                            <h1 className="font-normal text-md">
                                                Assigned Date
                                            </h1>
                                            <input
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        start: e.target.value,
                                                    })
                                                }
                                                type="date"
                                                id="start"
                                                name="trip-start"
                                                className="pr-1 border border-gray-200 rounded-md form-select"
                                            ></input>
                                        </div> */}

                                        <AssignedByListBox
                                            label="Assign service to"
                                            employees={managers || []}
                                            assignedBy={form.assignedEmployee}
                                            onChange={onHandleChangeAssignEmployee}
                                        />

                                        {form.assignedEmployee && (
                                            <div className="flex flex-col mt-5 sm:mt-6">
                                                <h1 className="font-normal text-md">
                                                    Due Date
                                                </h1>
                                                <input
                                                    onChange={onDueDateChange}
                                                    type="date"
                                                    id="dueDate"
                                                    name="dueDate"
                                                    className="pr-1 border border-gray-200 rounded-md form-select"
                                                    value={form.end}
                                                ></input>
                                            </div>
                                        )}

                                        {!displayNotes ? (
                                            <button
                                                className="inline-flex items-center p-1 mt-4 text-base text-white bg-teal-500 border border-transparent rounded-sm shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                                                onClick={toggleNotes}
                                            >
                                                Add notes
                                            </button>
                                        ) : (
                                            <button
                                                className="inline-flex items-center p-1 mt-4 text-base text-white bg-red-500 border border-transparent rounded-sm shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                                onClick={toggleNotes}
                                                type="button"
                                            >
                                                Hide notes
                                            </button>
                                        )}
                                        {displayNotes && (
                                            <>
                                                <InputTextAreaModal
                                                    label="Permanent Notes"
                                                    name="notes"
                                                    onChange={(e) =>
                                                        setForm({
                                                            ...form,
                                                            notes: e.target.value,
                                                        })
                                                    }
                                                    value={form.notes}
                                                    ref={textAreaRef}
                                                />
                                                <ButtonAddTimestamp
                                                    onHandleClick={() =>
                                                        onHandleClickAddTimestamp(
                                                            "notes",
                                                        )
                                                    }
                                                />
                                            </>
                                        )}
                                    </div>
                                    <div className="mt-5 sm:mt-6">
                                        <button
                                            type="button"
                                            className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-teal-500 border border-transparent rounded-md shadow-sm hover:bg-teal-600 focus:outline-none focus:ring-0 sm:text-sm"
                                            onClick={() => handleSubmit()}
                                        >
                                            Clone Service
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
        </section>
    );
}
